import type { OverviewResponse } from "@contracts/admin";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, ArrowRight, CheckCircle2, Clock3, Film, RadioTower, UploadCloud } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { apiRequest } from "@frontend/api/client";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@frontend/components/ui";
import { formatRelative, humanizeAction } from "@frontend/lib/format";

export function OverviewPage() {
  const query = useQuery({
    queryKey: ["overview"],
    queryFn: () => apiRequest<OverviewResponse>("/api/v1/admin/overview"),
    refetchInterval: 30_000,
  });

  if (query.isLoading) {
    return <LoadingState />;
  }
  if (query.isError || !query.data) {
    return <ErrorState message="A visão geral não respondeu." retry={() => void query.refetch()} />;
  }
  const data = query.data;

  return (
    <>
      <PageHeader
        title="Visão geral"
        actions={<span aria-live="polite" className="refresh-note"><i /> Atualizado {formatRelative(data.generated_at)}</span>}
      />

      <section className="metric-grid" aria-label="Indicadores principais">
        <MetricCard icon={RadioTower} label="Coletores reportando" value={data.collectors.reporting} detail={`${data.collectors.stale} ativos sem contato · ${data.collectors.pending} pendentes`} tone={data.collectors.stale + data.collectors.pending > 0 ? "warning" : "success"} />
        <MetricCard icon={CheckCircle2} label="Câmeras saudáveis" value={data.cameras.ok} detail={`${data.cameras.enabled} câmeras habilitadas`} tone="success" />
        <MetricCard icon={AlertTriangle} label="Alertas de imagem" value={data.cameras.black + data.cameras.error} detail={`${data.cameras.black} pretas · ${data.cameras.error} com erro`} tone={data.cameras.black + data.cameras.error > 0 ? "danger" : "neutral"} />
        <MetricCard icon={UploadCloud} label="Fila de upload" value={data.upload_queue_pending} detail={`${data.frames.pending} frames aguardando conclusão`} tone={data.upload_queue_pending > 0 ? "warning" : "neutral"} />
      </section>

      <section className="overview-grid">
        <div className="overview-column">
          <article className="panel health-panel">
            <header className="panel__header">
              <h2>Saúde das câmeras</h2>
              <Link className="text-link" to="/collectors">Ver coletores <ArrowRight size={16} /></Link>
            </header>
            <div className="health-summary">
              <div
                aria-label={`${percentage(data.cameras.ok, data.cameras.enabled)}% das câmeras habilitadas estão saudáveis`}
                className="health-donut"
                role="img"
                style={{ "--health": `${percentage(data.cameras.ok, data.cameras.enabled)}%` } as React.CSSProperties}
              >
                <div><strong>{percentage(data.cameras.ok, data.cameras.enabled)}%</strong><span>saudáveis</span></div>
              </div>
              <div className="health-legend">
                <Legend label="Operando normalmente" value={data.cameras.ok} tone="success" />
                <Legend label="Frame preto" value={data.cameras.black} tone="dark" />
                <Legend label="Com erro" value={data.cameras.error} tone="danger" />
                <Legend label="Sem diagnóstico" value={data.cameras.unknown} tone="neutral" />
                <Legend label="Desabilitadas" value={data.cameras.disabled} tone="muted" />
              </div>
            </div>
          </article>

          <nav aria-label="Atalhos" className="quick-links">
            <Link to="/collectors"><RadioTower size={19} /><span><strong>Coletores</strong><small>{data.collectors.total} cadastrados</small></span><ArrowRight size={17} /></Link>
            <Link to="/frames"><Film size={19} /><span><strong>Frames</strong><small>{data.frames.completed} concluídos</small></span><ArrowRight size={17} /></Link>
          </nav>
        </div>

        <article className="panel">
          <header className="panel__header">
            <h2>Atividade recente</h2>
          </header>
          <div className="activity-list">
            {data.recent_audit.length === 0 ? (
              <div className="activity-empty"><Clock3 size={19} /> Nenhuma atividade recente</div>
            ) : data.recent_audit.map((event) => (
              <div className="activity-item" key={event.id}>
                <span className="activity-item__icon"><Activity size={17} /></span>
                <div><strong>{humanizeAction(event.action)}</strong><span>{event.actor_display_name ?? "Automação administrativa"} · {formatRelative(event.created_at)}</span></div>
                <StatusBadge tone={auditActorTone(event.actor_type)}>{auditActorLabel(event.actor_type)}</StatusBadge>
              </div>
            ))}
          </div>
        </article>
      </section>
    </>
  );
}

function MetricCard({ icon: Icon, label, value, detail, tone }: { icon: LucideIcon; label: string; value: number; detail: string; tone: "success" | "warning" | "danger" | "neutral" }) {
  return <article className={`metric-card metric-card--${tone}`}><span className="metric-card__icon"><Icon size={20} /></span><div><span>{label}</span><strong>{value.toLocaleString("pt-BR")}</strong><small>{detail}</small></div></article>;
}

function Legend({ label, value, tone }: { label: string; value: number; tone: string }) {
  return <div className="legend-row"><span><i className={`legend-dot legend-dot--${tone}`} />{label}</span><strong>{value}</strong></div>;
}

function percentage(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

function auditActorLabel(actorType: OverviewResponse["recent_audit"][number]["actor_type"]): string {
  if (actorType === "admin_user") return "Usuário";
  if (actorType === "collector_activation") return "Ativação";
  return "Token";
}

function auditActorTone(actorType: OverviewResponse["recent_audit"][number]["actor_type"]): "info" | "warning" | "neutral" {
  if (actorType === "admin_user") return "info";
  if (actorType === "collector_activation") return "warning";
  return "neutral";
}
